'use client';

import { type ProductBrief } from '@/domain/products/types';
import { type PlatformVersionDTO, type RoadMapUpdate } from '@/domain/roadmap/types';
import { toDateString } from '@/utils/datetime';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Autocomplete } from '@andritzot/metris-web-ui/inputs/autocomplete';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { type Control, Controller } from 'react-hook-form';

import { FieldActionsWrapper } from './shared/FieldActionsWrapper';

interface AffectedVersionsFieldProps {
  viewOnly: boolean;
  control: Control<RoadMapUpdate, unknown>;
  product: ProductBrief | null;
  roadmapVersions: PlatformVersionDTO[];
  required: boolean;
}

const versionLabel = (v: PlatformVersionDTO) => `${v.name} (${toDateString(v.releaseDate)})`;

export function AffectedVersionsField({
  viewOnly,
  control,
  product,
  roadmapVersions,
  required,
}: Readonly<AffectedVersionsFieldProps>) {
  const noProductSelected = product == null;
  const hasNoVersions = product != null && roadmapVersions.length === 0;

  return (
    <Box sx={{ py: 0.75, width: '100%' }}>
      <Controller
        name="affectedVersions"
        control={control}
        rules={{ required }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <FieldActionsWrapper>
              <Autocomplete<PlatformVersionDTO, true, false, false>
                multiple
                value={value ?? []}
                onChange={(_, newValue) => onChange(newValue)}
                options={roadmapVersions}
                getOptionLabel={versionLabel}
                isOptionEqualToValue={(opt, v) => opt.idPlatformVersion === v.idPlatformVersion}
                disabled={viewOnly || noProductSelected || roadmapVersions.length === 0}
                label={`Affected Versions${required ? ' *' : ''}`}
                fullWidth
                size="small"
                sx={error && required ? { '& .MuiOutlinedInput-root': { borderColor: 'error.main' } } : undefined}
              />
            </FieldActionsWrapper>
            {!viewOnly && noProductSelected && (
              <Typography variant="caption" color="warning.main">
                Select a product first
              </Typography>
            )}
            {!viewOnly && hasNoVersions && (
              <Typography variant="caption" color="warning.main">
                {`No versions saved for the product ${product?.product}`}
              </Typography>
            )}
            {error && required && (
              <Typography variant="caption" color="error.main">
                Required
              </Typography>
            )}
          </>
        )}
      />
    </Box>
  );
}
